/*
 * TPS — visible price sort toggle on every product listing page.
 *
 * The theme already supports price-ascending and price-descending, but they are buried
 * inside the "Sort" dropdown alongside seven other options. This surfaces the two people
 * actually use as a one-click segmented control.
 *
 * TWO mount points, because desktop and mobile lay the facets out completely differently:
 *
 *   Desktop — .facets__form is a nowrap flex row (filters | count | sort | layout) with
 *   spare width, so the toggle slots in beside the Sort control.
 *
 *   Mobile — that row collapses to a "Filter" button that opens a drawer, so a toggle
 *   mounted there is invisible until the drawer is opened, which defeats the point.
 *   Instead a standalone bar goes directly above the product grid.
 *
 * Both are always rendered; CSS shows exactly one per breakpoint. That is deterministic —
 * probing offsetParent to decide at runtime races the theme's own layout work.
 *
 * Navigation rather than the hidden <select>: it preserves every other facet already in
 * the query string, survives the grid re-rendering, and is shareable and bookmarkable.
 *
 * Note on the observer: injecting the toggle is itself a DOM mutation, so the observer
 * must be disconnected around the write and the write must be idempotent (see the
 * data-state check). Without both, this loops forever and locks the page.
 */

const LOW_TO_HIGH = 'price-ascending';
const HIGH_TO_LOW = 'price-descending';

let observer = null;

function currentSort() {
  return new URLSearchParams(window.location.search).get('sort_by') || '';
}

function urlForSort(value) {
  const url = new URL(window.location.href);
  if (currentSort() === value) {
    // Clicking the active option clears it and returns to the collection default.
    url.searchParams.delete('sort_by');
  } else {
    url.searchParams.set('sort_by', value);
  }
  url.searchParams.delete('page');
  return url.toString();
}

/** @param {'toolbar'|'bar'} variant */
function buildToggle(variant) {
  const active = currentSort();

  const wrap = document.createElement('div');
  wrap.className = 'tps-price-sort tps-price-sort--' + variant;
  wrap.dataset.state = active;
  wrap.setAttribute('role', 'group');
  wrap.setAttribute('aria-label', 'Sort by price');

  const label = document.createElement('span');
  label.className = 'tps-price-sort__label';
  label.textContent = 'Price';
  wrap.appendChild(label);

  [
    { value: LOW_TO_HIGH, text: 'Low to high', aria: 'Sort by price, low to high' },
    { value: HIGH_TO_LOW, text: 'High to low', aria: 'Sort by price, high to low' },
  ].forEach(({ value, text, aria }) => {
    const button = document.createElement('a');
    button.className = 'tps-price-sort__option';
    button.href = urlForSort(value);
    button.textContent = text;
    button.setAttribute('aria-label', aria);
    if (active === value) {
      button.dataset.active = 'true';
      button.setAttribute('aria-current', 'true');
    }
    wrap.appendChild(button);
  });

  return wrap;
}

function findSortPanel(root) {
  // The theme renders sort as a <details class="facets__panel"> whose summary starts "Sort".
  return [...root.querySelectorAll('details.facets__panel')].find((panel) => {
    const summary = panel.querySelector('summary');
    return summary && summary.textContent.trim().toLowerCase().startsWith('sort');
  });
}

/**
 * @returns {{parent: Element, before: Element|null, variant: 'toolbar'|'bar'}[]}
 */
function findMounts() {
  const mounts = [];

  // Desktop: beside the Sort control inside the facets toolbar.
  document.querySelectorAll('.facets__form').forEach((form) => {
    const sorting = form.querySelector(':scope > .sorting-filter');
    if (sorting) mounts.push({ parent: form, before: sorting, variant: 'toolbar' });
  });

  if (mounts.length === 0) {
    const panel = findSortPanel(document);
    if (panel && panel.parentElement) {
      mounts.push({ parent: panel.parentElement, before: panel, variant: 'toolbar' });
    }
  }

  // Mobile: a standalone bar immediately above the product grid.
  const grid = document.querySelector('.product-grid');
  if (grid && grid.parentElement) {
    mounts.push({ parent: grid.parentElement, before: grid, variant: 'bar' });
  }

  return mounts;
}

function inject() {
  // Only on pages that actually list products.
  if (!document.querySelector('[name="sort_by"]')) return;

  findMounts().forEach(({ parent, before, variant }) => {
    const existing = parent.querySelector(':scope > .tps-price-sort--' + variant);

    if (existing) {
      // Idempotent: only rewrite when the active sort actually changed. This is what
      // stops the observer from chasing its own mutations.
      if (existing.dataset.state === currentSort()) return;
      existing.replaceWith(buildToggle(variant));
      return;
    }

    parent.insertBefore(buildToggle(variant), before);
  });
}

function safeInject() {
  if (observer) observer.disconnect();
  try {
    inject();
  } finally {
    if (observer) observer.observe(document.documentElement, { childList: true, subtree: true });
  }
}

function start() {
  observer = new MutationObserver(safeInject);
  safeInject();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}
